import moment from 'moment';
import {
  AgentAssistantAcwViewModel,
  AgentAssistantAfterCallWorkStateModel,
  UserInteractionOutcome,
} from '@nimbus-library/features/agent-assistant/models/agent-assistant.model';
import {
  IUserSessionLive,
  UserSessionOutcome,
  UserSessionState,
} from '@nimbus-global/features/reportingLiveData/models/user-session.model';
import { isUnifyCall } from '@nimbus-library/features/shared/models/participant-info/i-combined-conversation.response';

export class AgentAssistantMapper {
  public mapAcwSession(userSessionLiveInAcw: IUserSessionLive): AgentAssistantAcwViewModel {
    const afterCallWorkState = this.mapToAfterCallWorkStateModel(userSessionLiveInAcw);
    // ACW extend/stop are Nimbus Extend capabilities: hide them for Unify calls.
    const isUnify = isUnifyCall(userSessionLiveInAcw.channel);
    return {
      ...afterCallWorkState,
      userSessionId: userSessionLiveInAcw.id,
      channel: userSessionLiveInAcw.channel,
      afterCallWorkAllowToStop: afterCallWorkState.afterCallWorkAllowToStop && !isUnify,
      afterCallWorkAllowToExtend:
        afterCallWorkState.afterCallWorkAllowToExtend &&
        !isUnify &&
        afterCallWorkState.state !== UserInteractionOutcome.InExtendedAfterCallWork,
      afterCallWorkUntil: moment(afterCallWorkState.afterCallWorkSince).add(
        moment.duration(afterCallWorkState.afterCallWorkDuration).asSeconds(),
        'seconds',
      ),
    };
  }

  private mapToAfterCallWorkStateModel(userSessionLiveInAcw: IUserSessionLive): AgentAssistantAfterCallWorkStateModel {
    return {
      unifiedConversationId: userSessionLiveInAcw.unifiedConversationId,
      conversationId: userSessionLiveInAcw.conversationId,
      teamId: userSessionLiveInAcw.teamId,
      initialModality: userSessionLiveInAcw.initialModality,
      channel: userSessionLiveInAcw?.channel ?? null,
      state: this.mapToUserInteractionOutcome(userSessionLiveInAcw.state, userSessionLiveInAcw.outcome),
      afterCallWorkSince: userSessionLiveInAcw.since,
      afterCallWorkDuration: userSessionLiveInAcw.afterCallWorkDuration,
      afterCallWorkAllowToStop: userSessionLiveInAcw.afterCallWorkAllowToStop,
      afterCallWorkAllowToExtend: userSessionLiveInAcw.afterCallWorkAllowToExtend,
    };
  }

  private mapToUserInteractionOutcome(state: UserSessionState, outcome: UserSessionOutcome): UserInteractionOutcome {
    if (state === UserSessionState.Terminated) {
      switch (outcome) {
        case UserSessionOutcome.Accepted:
          return UserInteractionOutcome.Accepted;
        case UserSessionOutcome.Declined:
          return UserInteractionOutcome.Declined;
        case UserSessionOutcome.Rona:
          return UserInteractionOutcome.Rona;
        case UserSessionOutcome.Canceled:
          return UserInteractionOutcome.Abandoned;
        case UserSessionOutcome.DestinationAccepted:
          return UserInteractionOutcome.DestinationAccepted;
        case UserSessionOutcome.DestinationDeclined:
          return UserInteractionOutcome.DestinationDeclined;
        case UserSessionOutcome.DestinationNotReached:
          return UserInteractionOutcome.DestinationNotReached;
        case UserSessionOutcome.OutboundUserNotAccepted:
          return UserInteractionOutcome.OutboundUserNotAccepted;
        case UserSessionOutcome.OutboundUserAborted:
          return UserInteractionOutcome.OutboundUserAborted;
        case UserSessionOutcome.Failed:
          return UserInteractionOutcome.Unknown;
        case UserSessionOutcome.TransferredInternally:
        case UserSessionOutcome.TransferredExternally:
        case UserSessionOutcome.ConsultedTransferred:
          return UserInteractionOutcome.Transferred;
        case UserSessionOutcome.InternalTransferFailed:
        case UserSessionOutcome.ExternalTransferFailed:
          return UserInteractionOutcome.TransferFailed;
      }
    }
    switch (state) {
      case UserSessionState.Selected:
        return UserInteractionOutcome.Incoming;
      case UserSessionState.DialingOut:
        return UserInteractionOutcome.DialingOut;
      case UserSessionState.Ongoing:
        return UserInteractionOutcome.Ongoing;
      case UserSessionState.Transferring:
        return UserInteractionOutcome.Transferring;
      case UserSessionState.Parked:
        return UserInteractionOutcome.Parked;
      case UserSessionState.PutOnHold:
        return UserInteractionOutcome.PutOnHold;
      case UserSessionState.InAfterCallWork:
        return UserInteractionOutcome.InAfterCallWork;
      case UserSessionState.InExtendedAfterCallWork:
        return UserInteractionOutcome.InExtendedAfterCallWork;
    }
  }
}
